import type { CSSProperties, FC } from "react";

import type { OrgChannelSummary } from "./useOrgChannels";

import { SIGNAL_HEX } from "../common/colorTokens";
import { Tooltip } from "../common/Tooltip";
import { getAvatarBackgroundColor } from "./InstanceAvatar";

// ---------------------------------------------------------------------------
// Shared channel-tag primitives.
// One chip pattern, three call shapes:
//   - <ChannelTag channel={...} />                     read-only chip
//   - <ChannelTag channel={...} onLeave={fn} />        chip with × leave btn
//   - <ChannelToggleTag channel={...} active=…
//                       OnToggle={fn} />               toggle pill (filled
//                                                       When active)
// All of them carry the channel's averaged-member accent color and the same
// Retro hover preview, so channel identity stays visually consistent across
// Chat, InfoTab, ChannelPickerDialog and SpawnDialog.
//
// `summary` (OrgChannelSummary) is preferred — it carries memberCount +
// Avatar seeds for the preview. When the caller only has a name + id (e.g.
// The BlackboardInstance.channels list before /user/channels resolves),
// Callers can pass `fallback={{name, id}}` and the tag will degrade to a
// Neutral chip without preview until summary data lands.
// ---------------------------------------------------------------------------

const NEUTRAL_ACCENT = SIGNAL_HEX.accent;

interface ChannelTagBaseProps {
  channel: OrgChannelSummary;
  className?: string;
  /** Show the # prefix (default: true). */
  showHash?: boolean;
}

interface ReadOnlyTagProps extends ChannelTagBaseProps {
  onLeave?: never;
  leaving?: never;
}

interface LeaveTagProps extends ChannelTagBaseProps {
  onLeave: () => void;
  leaving?: boolean;
}

type ChannelTagProps = ReadOnlyTagProps | LeaveTagProps;

export const ChannelTag: FC<ChannelTagProps> = ({
  channel,
  className = "",
  showHash = true,
  onLeave,
  leaving,
}) => {
  const accent = channel.accentColor;
  const teamsBreadcrumb = getTeamsBreadcrumb(channel);
  return (
    <Tooltip
      side="top"
      align="start"
      offset={6}
      content={<ChannelPreview channel={channel} />}
      tooltipClassName="border bg-white/95 p-0 backdrop-blur dark:bg-slate-950/95"
      maxWidthClassName="max-w-[260px]"
    >
      <span
        className={`group inline-flex items-center gap-1 border py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-colors ${
          onLeave ? "pl-2 pr-1" : "px-2"
        } ${className}`}
        style={chipStyle(accent)}
      >
        {showHash && <span style={{ color: `${accent}b3` }}>#</span>}
        {channel.displayName}
        <ProcessToolsChip status={channel.environmentStatus} />
        {teamsBreadcrumb && (
          <>
            <span className="text-slate-500" aria-hidden>
              ›
            </span>
            <span
              className="max-w-[16rem] truncate normal-case tracking-normal text-cyan-800/95 dark:text-cyan-200/90"
              title={teamsBreadcrumb.conversationId}
            >
              {teamsBreadcrumb.label}
            </span>
          </>
        )}
        {onLeave && (
          <button
            type="button"
            disabled={leaving}
            aria-label={`Leave #${channel.displayName}`}
            onClick={(e) => {
              e.stopPropagation();
              onLeave();
            }}
            className="ml-0.5 flex h-4 w-4 items-center justify-center opacity-0 transition-colors hover:text-rose-400 group-hover:opacity-100 disabled:opacity-40"
            style={{ color: `${accent}80` }}
          >
            {leaving ? "…" : "×"}
          </button>
        )}
      </span>
    </Tooltip>
  );
};

const ProcessToolsChip: FC<{ status: string | null }> = ({ status }) => {
  const isOn = status === "running";
  return (
    <span
      className={`ml-1 border px-1 py-px text-[8px] tracking-[0.16em] ${
        isOn
          ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-700 dark:text-cyan-300"
          : "border-slate-400/30 text-slate-400 dark:border-slate-600/50 dark:text-slate-500"
      }`}
      title={
        isOn
          ? "Process tools available in this channel"
          : "Process tools unavailable: channel environment is not running"
      }
    >
      PROC {isOn ? "ON" : "OFF"}
    </span>
  );
};

interface ChannelToggleTagProps {
  channel: OrgChannelSummary;
  active: boolean;
  onToggle: () => void;
  className?: string;
}

/** Toggle variant — fills with accent when active, outlined when inactive.
 *  Used in spawn flows where the user picks which channels to join. */
export const ChannelToggleTag: FC<ChannelToggleTagProps> = ({
  channel,
  active,
  onToggle,
  className = "",
}) => {
  const accent = channel.accentColor;
  const style: CSSProperties = active
    ? {
        backgroundColor: `${accent}26`,
        borderColor: `${accent}99`,
        color: accent,
      }
    : {
        backgroundColor: "transparent",
        borderColor: `${accent}40`,
        color: `${accent}99`,
      };
  return (
    <Tooltip
      side="top"
      align="start"
      offset={6}
      content={<ChannelPreview channel={channel} />}
      tooltipClassName="border bg-white/95 p-0 backdrop-blur dark:bg-slate-950/95"
      maxWidthClassName="max-w-[260px]"
    >
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-colors ${className}`}
        style={style}
      >
        <span style={{ color: active ? accent : `${accent}80`, opacity: 0.7 }}>
          #
        </span>
        {channel.displayName}
      </button>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Inline chip styling (shared between read-only and leave variants).
// ---------------------------------------------------------------------------

function chipStyle(accent: string): CSSProperties {
  return {
    backgroundColor: `${accent}1a`,
    borderColor: `${accent}55`,
    color: accent,
  };
}

export function getTeamsBreadcrumb(
  channel: OrgChannelSummary,
): { conversationId: string; label: string } | null {
  if (
    !channel.activeTeamsBindingId ||
    !channel.activeTeamsConversationId ||
    !channel.activeTeamsConversationLabel
  ) {
    return null;
  }
  return {
    conversationId: channel.activeTeamsConversationId,
    label: channel.activeTeamsConversationLabel,
  };
}

// ---------------------------------------------------------------------------
// Preview tooltip — same retro frame used across all surfaces.
// ---------------------------------------------------------------------------

const ChannelPreview: FC<{ channel: OrgChannelSummary }> = ({ channel }) => {
  const created = new Date(channel.createdAt);
  const now = new Date();
  const ageDays = Math.max(
    0,
    Math.floor((now.getTime() - created.getTime()) / 86_400_000),
  );
  const ageLabel = formatChannelAge(ageDays);
  const accent = channel.accentColor || NEUTRAL_ACCENT;

  return (
    <div className="relative w-full px-3 py-2.5">
      {(["tl", "tr", "bl", "br"] as const).map((pos) => (
        <span
          key={pos}
          aria-hidden
          className={`pointer-events-none absolute h-1.5 w-1.5 ${cornerClassName(
            pos,
          )}`}
          style={{ borderColor: accent }}
        />
      ))}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,1) 0 1px, transparent 1px 3px)",
        }}
      />
      <div className="relative">
        <p
          className="font-mono text-[9px] font-bold uppercase tracking-[0.25em]"
          style={{ color: `${accent}cc` }}
        >
          [ CHANNEL ]
        </p>
        <p className="mt-0.5 font-mono text-sm font-semibold text-slate-900 dark:text-white">
          <span style={{ color: `${accent}b3` }}>#</span>
          {channel.displayName}
        </p>
        {channel.activeTeamsConversationId &&
          channel.activeTeamsConversationLabel && (
            <div className="mt-2 border border-cyan-400/20 bg-cyan-400/5 px-2 py-1">
              <p className="font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-cyan-700/90 dark:text-cyan-300/80">
                [ TEAMS ]
              </p>
              <p className="mt-0.5 font-mono text-[11px] text-cyan-900 dark:text-cyan-100">
                {channel.activeTeamsConversationLabel}
              </p>
              <p className="mt-1 break-all font-mono text-[9px] text-slate-500">
                {channel.activeTeamsConversationId}
              </p>
            </div>
          )}
        <div className="mt-2 grid grid-cols-2 gap-2">
          <PreviewStat
            label="MEMBERS"
            value={String(channel.memberCount)}
            color={accent}
          />
          <PreviewStat label="AGE" value={ageLabel} color={SIGNAL_HEX.info} />
        </div>
        {channel.memberAvatarSeeds.length > 0 && (
          <MemberPalette seeds={channel.memberAvatarSeeds} />
        )}
      </div>
    </div>
  );
};

function formatChannelAge(ageDays: number): string {
  if (ageDays === 0) {
    return "today";
  }
  if (ageDays === 1) {
    return "yesterday";
  }
  if (ageDays < 30) {
    return `${ageDays}d ago`;
  }
  return `${Math.floor(ageDays / 30)}mo ago`;
}

function cornerClassName(pos: "tl" | "tr" | "bl" | "br"): string {
  switch (pos) {
    case "tl": {
      return "left-1 top-1 border-l border-t";
    }
    case "tr": {
      return "right-1 top-1 border-r border-t";
    }
    case "bl": {
      return "bottom-1 left-1 border-b border-l";
    }
    case "br": {
      return "bottom-1 right-1 border-b border-r";
    }
  }
}

const PreviewStat: FC<{ label: string; value: string; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div
    className="border bg-slate-100/60 px-2 py-1 dark:bg-slate-900/40"
    style={{ borderColor: `${color}33` }}
  >
    <p className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">
      {label}
    </p>
    <p
      className="mt-0.5 font-mono text-xs font-semibold tabular-nums"
      style={{ color }}
    >
      {value}
    </p>
  </div>
);

const MemberPalette: FC<{ seeds: string[] }> = ({ seeds }) => {
  const visible = seeds.slice(0, 6);
  const overflow = seeds.length - visible.length;
  return (
    <div className="mt-2 flex items-center gap-0.5">
      {visible.map((seed, i) => (
        <span
          key={`${seed}-${i}`}
          className="h-2 w-2 border border-slate-900"
          style={{ backgroundColor: getAvatarBackgroundColor(seed) }}
        />
      ))}
      {overflow > 0 && (
        <span className="ml-1 font-mono text-[9px] tabular-nums text-slate-500">
          +{overflow}
        </span>
      )}
    </div>
  );
};
